/**
 * Native onboarding - shown on first cold-launch on iOS/Android.
 * Web users never see this (NativeRootGate routes them to ConnectHome).
 *
 * Flow (per design):
 *   1. Hero screen  - Logo, headline, "Get Started" + "Take a quick tour".
 *   2. Tour (opt-in) - 3 product slides → target-country picker → legal card.
 *   3. Finish        - record completion, route to /connect-portal/auth.
 *
 * Completion is persisted ONLY when the user finishes or skips intentionally.
 * (Previous versions marked completion on mount, which silently swallowed the
 * intro for any user who relaunched the app mid-flow.)
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useSafeArea } from "@/hooks/useSafeArea";
import elefaAppIcon from "@/assets/elefa-app-icon.webp";
import { useDeviceContacts, type ContactPermission } from "@/hooks/useDeviceContacts";
import { ContactPermissionSheet } from "@/components/connect/ContactPermissionSheet";
import { TargetCountryStep, persistTargetCountry, type TargetCountry } from "@/components/native/TargetCountryStep";
import useNativeStatusBar from "@/hooks/useNativeStatusBar";
import {
  IllustrationCall,
  IllustrationPrivacy,
  IllustrationWallet,
} from "@/components/native/onboarding/OnboardingIllustrations";

const STORAGE_KEY = "elefa.native.onboarding.completed.v1";

export function hasCompletedNativeOnboarding(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markCompleted() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* noop */
  }
}

type Stage = "hero" | "tour";

export default function NativeOnboarding() {
  const { t } = useTranslation("portal");
  const navigate = useNavigate();
  const insets = useSafeArea();
  const contacts = useDeviceContacts();
  useNativeStatusBar('light', '#FFFFFF');

  const [stage, setStage] = useState<Stage>("hero");
  const [index, setIndex] = useState(0);
  const [permission] = useState<ContactPermission>("prompt");
  const [permissionSheetOpen, setPermissionSheetOpen] = useState(false);
  const [pendingFinish, setPendingFinish] = useState(false);
  const [targetCountry, setTargetCountry] = useState<TargetCountry | null>(null);

  const slides = useMemo(
    () => [
      {
        Illustration: IllustrationCall,
        title: t("onboardingSlide1Title", "They just need a phone. Not an app."),
        body: t(
          "onboardingSlide1Body",
          "WhatsApp needs both sides online. elefa dials their actual number. Basic phones, landlines, no data required on their end."
        ),
      },
      {
        Illustration: IllustrationPrivacy,
        title: t("onboardingSlide2Title", "Your calls are private and your data is yours."),
        body: t(
          "onboardingSlide2Body",
          "We do not sell your data or share it with third parties. Your call history stays on your account and is only visible to you."
        ),
      },
      {
        Illustration: IllustrationWallet,
        title: t("onboardingSlide3Title", "Top up when you can. Call whenever you want."),
        body: t(
          "onboardingSlide3Body",
          "Your credit does not expire. No monthly fees or minimum spend. You see the rate per minute before every call so there are no surprises."
        ),
      },
    ],
    [t]
  );

  // Virtual step layout in tour mode:
  //   [0..slides.length-1] product slides
  //   [slides.length]      country picker (final step)
  // Legal acceptance is handled inline on the auth/phone-number screen via the
  // standing "By continuing you agree to Terms & Privacy" disclosure — keeping
  // onboarding light while still legally compliant.
  const COUNTRY_STEP = slides.length;
  const TOTAL_STEPS = slides.length + 1;

  const isCountryStep = stage === "tour" && index === COUNTRY_STEP;
  const isLast = isCountryStep;

  // Logo intro animation on first paint
  const [logoIn, setLogoIn] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setLogoIn(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const goToSignup = () => {
    markCompleted();
    try { localStorage.setItem("elefa.native.contacts.asked.v1", "1"); } catch { /* noop */ }
    navigate("/connect-portal/auth", { replace: true });
  };

  const finish = () => {
    let alreadyAsked = false;
    try { alreadyAsked = localStorage.getItem("elefa.native.contacts.asked.v1") === "1"; } catch { /* noop */ }

    if (contacts.isSupported && !alreadyAsked) {
      setPendingFinish(true);
      setPermissionSheetOpen(true);
      return;
    }

    goToSignup();
  };

  const handleAllowContacts = () => {
    try { localStorage.setItem("elefa.native.contacts.consent.v1", "1"); } catch { /* noop */ }
    setPermissionSheetOpen(false);
    goToSignup();
  };

  const handleCloseContacts = () => {
    setPermissionSheetOpen(false);
    if (pendingFinish) goToSignup();
  };

  const handleOpenSettings = () => {
    setPermissionSheetOpen(false);
    if (pendingFinish) goToSignup();
  };

  const next = () => {
    if (isLast) finish();
    else setIndex((i) => i + 1);
  };

  const startTour = () => {
    setStage("tour");
    setIndex(0);
  };

  const handleSelectTarget = (c: TargetCountry | null) => {
    setTargetCountry(c);
    persistTargetCountry(c);
  };

  const slide = stage === "tour" && index < slides.length ? slides[index] : null;

  // ───────────────────────── HERO STAGE ─────────────────────────
  if (stage === "hero") {
    return (
      <div
        className="min-h-[100dvh] bg-background flex flex-col relative overflow-hidden"
        style={{
          paddingTop: `max(${insets.top}px, env(safe-area-inset-top, 0px))`,
          paddingBottom: `max(${insets.bottom + 16}px, calc(env(safe-area-inset-bottom, 0px) + 16px))`,
          paddingLeft: `env(safe-area-inset-left, 0px)`,
          paddingRight: `env(safe-area-inset-right, 0px)`,
        }}
      >
        {/* Soft branded ambient backdrop */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.18),transparent_55%),radial-gradient(ellipse_at_bottom,hsl(var(--elefa-gold)/0.10),transparent_55%)]" />
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: logoIn ? 1 : 0, y: logoIn ? 0 : -8 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="mb-24 inline-flex max-w-full items-center justify-center"
            aria-label="App logo"
          >
            <img
              src={elefaAppIcon}
              alt=""
              width={72}
              height={72}
              decoding="sync"
              loading="eager"
              className="h-16 w-16 shrink-0 rounded-[22%] object-cover shadow-sm-soft"
            />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
            className="text-4xl sm:text-5xl font-bold tracking-normal leading-[1.05] text-foreground max-w-sm"
          >
            {t("onboardingHeroTitle", "Your family doesn't need internet to receive your call.")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            className="mt-5 text-base sm:text-lg text-muted-foreground max-w-[320px]"
          >
            {t(
              "onboardingHeroBody",
              "elefa calls any phone, any number. No app needed on their end. Just dial."
            )}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.45, ease: "easeOut" }}
          className="px-6 space-y-3"
        >
          <Button
            size="lg"
            className="w-full h-14 rounded-2xl text-base font-semibold"
            onClick={finish}
          >
            {t("onboardingHeroCta", "Call home")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <button
            type="button"
            onClick={startTour}
            className="w-full h-12 rounded-2xl text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("onboardingTakeTour", "See how it works")}
          </button>
        </motion.div>

        <ContactPermissionSheet
          isOpen={permissionSheetOpen}
          onClose={handleCloseContacts}
          onAllow={handleAllowContacts}
          permission={permission}
          onOpenSettings={handleOpenSettings}
          isSupported={contacts.isSupported}
          isRequesting={false}
        />
      </div>
    );
  }

  // ───────────────────────── TOUR STAGE ─────────────────────────
  return (
    <div
      className="min-h-[100dvh] bg-background flex flex-col"
      style={{
        paddingTop: `max(${insets.top}px, env(safe-area-inset-top, 0px))`,
        paddingBottom: `max(${insets.bottom + 16}px, calc(env(safe-area-inset-bottom, 0px) + 16px))`,
        paddingLeft: `env(safe-area-inset-left, 0px)`,
        paddingRight: `env(safe-area-inset-right, 0px)`,
      }}
    >
        <header className="flex items-center justify-between px-5 py-3">
          <img
            src={elefaAppIcon}
            alt=""
            width={44}
            height={44}
            decoding="sync"
            loading="eager"
            className="h-11 w-11 rounded-[22%] object-cover shadow-sm-soft"
          />
        <button
          type="button"
          onClick={finish}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Skip onboarding"
        >
          {t("skip", "Skip")}
        </button>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <AnimatePresence mode="wait">
          {isCountryStep ? (
            <TargetCountryStep
              key="country-step"
              selected={targetCountry}
              onSelect={handleSelectTarget}
              onContinue={finish}
              onSkip={finish}
            />
          ) : slide ? (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="w-full max-w-sm flex flex-col items-center text-center"
            >
              <div className="aspect-square w-full max-w-[280px] mb-8">
                <slide.Illustration className="w-full h-full" />
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-3 leading-tight">
                {slide.title}
              </h1>
              <p className="text-base text-muted-foreground max-w-[300px]">
                {slide.body}
              </p>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      <div className="flex justify-center gap-2 mb-6">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Go to step ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-primary" : "w-2 bg-muted"
            }`}
          />
        ))}
      </div>

      {!isCountryStep && (
        <div className="px-6">
          <Button
            size="lg"
            className="w-full h-14 rounded-2xl text-base font-semibold"
            onClick={next}
          >
            {isLast ? t("getStarted", "Get Started") : t("next", "Next")}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      )}

      <ContactPermissionSheet
        isOpen={permissionSheetOpen}
        onClose={handleCloseContacts}
        onAllow={handleAllowContacts}
        permission={permission}
        onOpenSettings={handleOpenSettings}
        isSupported={contacts.isSupported}
        isRequesting={false}
      />

    </div>
  );
}
